import { getTask } from "actions";
import { memoAC } from "./memoize";
import axios from "axios";
import axiosServices from "v2/utils/axios";
import Datasource from "lib/datasource";
import { getAuthTokenFromCookie } from 'lib/hooks';

const v2Headers = () => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${getAuthTokenFromCookie()}`,
});

export const putCondition = memoAC((condition: any) => (dispatch: any) => {
  dispatch({ type: "PUT_CONDITION_REQUEST" });
  axios.put(`${process.env.REACT_APP_API_ENDPOINT_V1}conditions/${condition.id}`, { condition }, {
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${getAuthTokenFromCookie()}`,
    }
  })
  .then((res) => {
    dispatch({ type: "PUT_CONDITION_SUCCESS" });
    dispatch(getTask(res.data.condition.task.id));
  })
  .catch((e) => { dispatch({ type: "PUT_CONDITION_FAILED", response: e }); });
});

export const reopenCondition = memoAC((id: string) => (dispatch: any) => {
  dispatch({ type: "REOPEN_CONDITION_REQUEST" });
  axios.put(`${process.env.REACT_APP_API_ENDPOINT_V1}conditions/${id}/reopen`, {}, {
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${getAuthTokenFromCookie()}`,
    }
  })
  .then((res) => {
    dispatch({ type: "REOPEN_CONDITION_SUCCESS" });
    dispatch(getTask(res.data.condition.task.id));
  })
  .catch((e) => { dispatch({ type: "REOPEN_CONDITION_FAILED", response: e }); });
});

export const dismissCondition = memoAC((id: string) => (dispatch: any) => {
  dispatch({ type: "DISMISS_CONDITION_REQUEST" });
  axios.put(`${process.env.REACT_APP_API_ENDPOINT_V1}conditions/${id}/dismiss`, {}, {
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${getAuthTokenFromCookie()}`,
    }
  })
  .then((res) => {
    dispatch({ type: "DISMISS_CONDITION_SUCCESS" });
    dispatch(getTask(res.data.condition.task.id));
  })
  .catch((e) => { dispatch({ type: "DISMISS_CONDITION_FAILED", response: e }); });
});

//TODO - deprecate accept and reject condition functions. Everything should be passed as an arbitrary response.
export const acceptCondition = memoAC((id: string) => (dispatch: any) => {
  dispatch({ type: "ACCEPT_CONDITION_REQUEST" });
  axios.put(`${process.env.REACT_APP_API_ENDPOINT_V1}conditions/${id}/accept`, {}, {
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${getAuthTokenFromCookie()}`,
    }
  })
  .then((res) => {
    dispatch({ type: "ACCEPT_CONDITION_SUCCESS" });
    dispatch(getTask(res.data.condition.task.id));
  })
  .catch((e) => { dispatch({ type: "ACCEPT_CONDITION_FAILED", response: e }); });
});
export const rejectCondition = memoAC((id: string) => (dispatch: any) => {
  dispatch({ type: "REJECT_CONDITION_REQUEST" });
  axios.put(`${process.env.REACT_APP_API_ENDPOINT_V1}conditions/${id}/reject`, {}, {
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${getAuthTokenFromCookie()}`,
    }
  })
  .then((res) => {
    dispatch({ type: "REJECT_CONDITION_SUCCESS" });
    dispatch(getTask(res.data.condition.task.id));
  })
  .catch((e) => { dispatch({ type: "REJECT_CONDITION_FAILED", response: e }); });
});



export const closeCondition = memoAC((id: string, response?: string) => (dispatch: any) => {
  dispatch({ type: "CLOSE_CONDITION_REQUEST" });
  axios.put(`${process.env.REACT_APP_API_ENDPOINT_V1}conditions/${id}/close`, {}, {
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${getAuthTokenFromCookie()}`,
    }
  })
  .then((res) => {
    dispatch(putCondition({ response, id }));
    dispatch({ type: "CLOSE_CONDITION_SUCCESS" });
    dispatch(getTask(res.data.condition.task.id));
  })
  .catch((e) => { dispatch({ type: "CLOSE_CONDITION_FAILED", response: e }); });
});

export const getCondition = memoAC((id: string) => (dispatch: any) => {
  dispatch({ type: "GET_CONDITION_REQUEST" });
  axios.get(`${process.env.REACT_APP_API_ENDPOINT_V2}conditions/${id}`, { headers: v2Headers() })
    .then((res) => {
      const condition = Datasource.getNormalizedData(res.data, "condition");
      dispatch({ type: "GET_CONDITION_SUCCESS", response: { status: 200 }, resource: condition });
    })
    .catch((e) => { dispatch({ type: "GET_CONDITION_FAILED", response: e }); });
});

export type AutoPopulateEntry = {
  value: string | null;
  label: string;
  group: string;
  // Optional display header for the entry's section. Present on dynamic groups
  // (e.g. per-tenancy "TENANCY 1 (…)") whose header can't come from a static
  // map; absent on the fixed groups, which fall back to a static label.
  group_label?: string;
};
export type AutoPopulateDictionary = Record<string, AutoPopulateEntry>;

// Fetches the read-only "Default Values" dictionary for a condition. This is the
// same data the server uses to auto-populate filled_data on first open; the client
// uses it ONLY to render the read-only "Default Values" reference tab. The client
// performs NO prefill — it trusts condition.filled_data from the server.
// Degrades gracefully to {} on any error so the form stays usable.
export const getConditionAutoPopulateDictionary =
  (id: string) => async (_dispatch: any): Promise<AutoPopulateDictionary> => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_ENDPOINT_V2}conditions/${id}/auto_populate_dictionary`,
        { headers: v2Headers() }
      );
      return (res?.data?.data?.attributes?.dictionary || {}) as AutoPopulateDictionary;
    } catch (e) {
      console.error("Failed to fetch auto-populate dictionary", e);
      return {};
    }
  };

export const updateConditionFillData = memoAC((id: string, filled_data: any, form_id?: string) => (dispatch: any) => {
  dispatch({ type: "PUT_CONDITION_FILL_REQUEST" });
  return axios.put(
    `${process.env.REACT_APP_API_ENDPOINT_V2}conditions/${id}`,
    { condition: { filled_data, form_id } },
    { headers: v2Headers() }
  )
    .then((res) => {
      const condition = Datasource.getNormalizedData(res.data, "condition");
      dispatch({ type: "PUT_CONDITION_FILL_SUCCESS", response: { status: 200 }, resource: condition });
      return condition;
    })
    .catch((e) => { dispatch({ type: "PUT_CONDITION_FILL_FAILED", response: e }); throw e; });
});

// Renders a transient preview PDF and returns a presigned URL. Does NOT
// persist a Document or update condition.generated_document_id — that only
// happens when the user clicks "Generate PDF & Set Up Signing"
// (generateConditionPdf below). Used by the FormFill bootstrap and the
// "Refresh preview" button.
export const previewConditionPdf = memoAC((id: string) => (_dispatch: any) => {
  return axios.post(
    `${process.env.REACT_APP_API_ENDPOINT_V2}conditions/${id}/preview_pdf`,
    {},
    { headers: v2Headers() }
  )
    .then((res) => {
      const url = res?.data?.data?.attributes?.preview_url as string | undefined;
      return url;
    })
    .catch((e) => {
      const detail = e?.response?.data?.errors?.detail || e?.response?.data?.error || e?.message;
      throw new Error(detail || "Failed to render preview");
    });
});

export const generateConditionPdf = memoAC((id: string, filled_data?: any) => (dispatch: any) => {
  dispatch({ type: "POST_CONDITION_GENERATE_PDF_REQUEST" });
  return axios.post(
    `${process.env.REACT_APP_API_ENDPOINT_V2}conditions/${id}/generate_pdf`,
    { filled_data },
    { headers: v2Headers() }
  )
    .then((res) => {
      const condition = Datasource.getNormalizedData(res.data, "condition");
      dispatch({ type: "POST_CONDITION_GENERATE_PDF_SUCCESS", response: { status: 200 }, resource: condition });
      return condition;
    })
    .catch((e) => {
      dispatch({ type: "POST_CONDITION_GENERATE_PDF_FAILED", response: e });
      const detail = e?.response?.data?.errors?.detail || e?.response?.data?.error || e?.message;
      const err = new Error(detail || "Failed to generate PDF");
      throw err;
    });
});

// Uploads a signed PDF for the condition. Two-step:
//   1. GET signed_document_upload → presigned S3 POST
//   2. Upload file to S3 directly with that POST
//   3. POST attach_signed_document → backend creates a Document and links it to the condition
export const uploadSignedDocument = (id: string, file: File) => async (dispatch: any) => {
  dispatch({ type: "POST_CONDITION_ATTACH_SIGNED_DOC_REQUEST" });
  try {
    // axiosServices has baseURL = REACT_APP_API_ENDPOINT_V2 + auth interceptor (refresh on 401)
    const presignedRes = await axiosServices.get(`conditions/${id}/signed_document_upload`, {
      params: { filename: file.name },
    });
    const presigned = presignedRes.data as { url: string; fields: Record<string, string>; key: string };

    const data = new FormData();
    data.append("key", presigned.fields["key"]);
    if (presigned.fields["Expires"]) data.append("Expires", presigned.fields["Expires"]);
    data.append("success_action_status", presigned.fields["success_action_status"]);
    data.append("policy", presigned.fields["policy"]);
    data.append("x-amz-credential", presigned.fields["x-amz-credential"]);
    data.append("x-amz-algorithm", presigned.fields["x-amz-algorithm"]);
    data.append("x-amz-date", presigned.fields["x-amz-date"]);
    data.append("x-amz-signature", presigned.fields["x-amz-signature"]);
    if (presigned.fields["acl"]) data.append("acl", presigned.fields["acl"]);
    data.append("file", file);
    // S3 upload — plain axios (no auth headers)
    const s3Res = await axios.post(presigned.url, data);
    if (s3Res.status < 200 || s3Res.status >= 300) {
      throw new Error(`S3 upload returned status ${s3Res.status}`);
    }

    const attachRes = await axiosServices.post(`conditions/${id}/attach_signed_document`, {
      key: presigned.key,
      name: file.name,
      mimetype: file.type || "application/pdf",
      filesize_bytes: file.size,
    });
    const condition = Datasource.getNormalizedData(attachRes.data, "condition");
    dispatch({ type: "POST_CONDITION_ATTACH_SIGNED_DOC_SUCCESS", response: { status: 200 }, resource: condition });
    return condition;
  } catch (e) {
    dispatch({ type: "POST_CONDITION_ATTACH_SIGNED_DOC_FAILED", response: e });
    throw e;
  }
};

// Rolls a generated create_form condition back to the pre-generation state so
// the Responsible user / an admin can amend the form and re-generate. The
// signer set is preserved server-side (Configure Signers pre-fills from it),
// but the round is reset: every signer returns to "pending" and their signed
// uploads are detached from the condition. The files are not deleted — they
// stay in the Documents tab tagged "form amended".
export const undoConditionGeneration = (id: string) => async (dispatch: any) => {
  dispatch({ type: "POST_CONDITION_UNDO_GENERATION_REQUEST" });
  try {
    const res = await axiosServices.post(`conditions/${id}/undo_generation`);
    const condition = Datasource.getNormalizedData(res.data, "condition");
    dispatch({ type: "POST_CONDITION_UNDO_GENERATION_SUCCESS", response: { status: 200 }, resource: condition });
    return condition;
  } catch (e) {
    dispatch({ type: "POST_CONDITION_UNDO_GENERATION_FAILED", response: e });
    throw e;
  }
};

export const configureConditionSigners = memoAC(
  (id: string, user_ids: string[], signing_order?: string, sharing_method?: string) => (dispatch: any) => {
    dispatch({ type: "POST_CONDITION_CONFIGURE_SIGNERS_REQUEST" });
    return axios.post(
      `${process.env.REACT_APP_API_ENDPOINT_V2}conditions/${id}/configure_signers`,
      { user_ids, signing_order, sharing_method },
      { headers: v2Headers() }
    )
      .then((res) => {
        const condition = Datasource.getNormalizedData(res.data, "condition");
        dispatch({ type: "POST_CONDITION_CONFIGURE_SIGNERS_SUCCESS", response: { status: 200 }, resource: condition });
        return condition;
      })
      .catch((e) => { dispatch({ type: "POST_CONDITION_CONFIGURE_SIGNERS_FAILED", response: e }); throw e; });
});

export const getConditions = memoAC(() => (dispatch: any) => {
  dispatch({ type: "GET_CONDITION_REQUEST" });
  axios.put(`${process.env.REACT_APP_API_ENDPOINT_V1}conditions`, {}, {
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${getAuthTokenFromCookie()}`,
    }
  })
  .then((res) => {
    dispatch({ type: "GET_CONDITIONS_SUCCESS", response: { status: 200 }, resource: res.data.conditions })
  })
  .catch((e) => { dispatch({ type: "GET_CONDITIONS_FAILED", response: e }); });
});
